import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  PhoneCall,
  Wrench,
  Clock,
  Users,
  ExternalLink,
  Sparkles,
  Camera,
  XCircle,
  CheckCircle2,
  Eye,
  Loader2,
  X,
} from 'lucide-react';
import { apiGet, apiPatch, apiPost } from '../shared/apiClient';
import { formatRome } from '../shared/tz';
import type { Impianto, Chiamata } from '../shared/types';
import { useToast } from '../hooks/useToast';

export interface ChiamataAttivaImpianto {
  id: string;
  impiantoId: string;
  dataOra: string;
  stato: string;
  inLavorazione: boolean;
  daCompletare: boolean;
  urgente: boolean;
  daSegnalazione: boolean;
  segnalatoDa?: string | null;
  guastoSegnalato?: string | null;
  descrizione?: string;
  avvisoSap?: string | null;
  squadraId?: string | null;
  squadra?: { id: string; nome: string } | null;
  ultimoReport?: {
    id: string;
    azioniEseguite: string;
    causaGuasto: string;
    esito: string;
    squadraNome: string;
    dataIntervento?: string | null;
  } | null;
}

export interface SegnalazioneApertaImpianto {
  id: string;
  impiantoId: string;
  squadraId?: string | null;
  squadraNome?: string | null;
  segnalatoDaNome?: string | null;
  titolo: string;
  descrizione?: string;
  fotoCount: number;
  createdAt: string;
}

export interface PermessiAzioniSquadra {
  miaSquadraId: string | null;
  canPrendiAperta: boolean;
  canPrendiDaTerminare: boolean;
  canPrendiSegnalazione: boolean;
}

interface AttivitaAperteResponse {
  haAttivita: boolean;
  totale: number;
  totaleChiamate: number;
  totaleSegnalazioni: number;
  chiamate: ChiamataAttivaImpianto[];
  segnalazioni: SegnalazioneApertaImpianto[];
  permessiAzioni: PermessiAzioniSquadra;
}

export function getStatoChiamataSquadraBadge(c: ChiamataAttivaImpianto, miaSquadraId?: string | null) {
  const isMia = !!(miaSquadraId && c.squadraId === miaSquadraId);
  if (c.stato === 'nuova') {
    return {
      label: c.daCompletare ? 'APERTA (Da completare)' : 'APERTA (In coda)',
      classes: 'bg-indigo-50 text-indigo-800 border-indigo-200',
      dot: 'bg-indigo-600',
    };
  }
  if (c.stato === 'assegnata' && c.inLavorazione) {
    return {
      label: isMia ? 'IN CARICO ALLA TUA SQUADRA' : 'PRESA IN CARICO (In lavorazione)',
      classes: 'bg-rose-50 text-rose-800 border-rose-200',
      dot: 'bg-rose-600',
    };
  }
  if (c.stato === 'assegnata') {
    return {
      label: isMia ? 'ASSEGNATA ALLA TUA SQUADRA' : 'ASSEGNATA',
      classes: 'bg-sky-50 text-sky-800 border-sky-200',
      dot: 'bg-sky-600',
    };
  }
  if (c.stato === 'sospesa') {
    return {
      label: 'DA TERMINARE (Sospesa)',
      classes: 'bg-amber-50 text-amber-900 border-amber-300',
      dot: 'bg-amber-600',
    };
  }
  return {
    label: c.stato.toUpperCase(),
    classes: 'bg-slate-100 text-slate-700 border-slate-200',
    dot: 'bg-slate-500',
  };
}

export function useAttivitaAperteImpiantoSquadra(impianto: Impianto | null) {
  const [loading, setLoading] = useState(false);
  const [chiamate, setChiamate] = useState<ChiamataAttivaImpianto[]>([]);
  const [segnalazioni, setSegnalazioni] = useState<SegnalazioneApertaImpianto[]>([]);
  const [permessiAzioni, setPermessiAzioni] = useState<PermessiAzioniSquadra>({
    miaSquadraId: null,
    canPrendiAperta: true,
    canPrendiDaTerminare: true,
    canPrendiSegnalazione: false,
  });
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    if (!impianto?.id) {
      setLoading(false);
      setChiamate([]);
      setSegnalazioni([]);
      setShowPopup(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    apiGet<AttivitaAperteResponse>(`/chiamate/attivita-aperte-impianto/${encodeURIComponent(impianto.id)}`)
      .then((res) => {
        if (cancelled) return;
        const data = res.data;
        const ch = Array.isArray(data?.chiamate) ? data.chiamate : [];
        const seg = Array.isArray(data?.segnalazioni) ? data.segnalazioni : [];
        setChiamate(ch);
        setSegnalazioni(seg);
        if (data?.permessiAzioni) {
          setPermessiAzioni(data.permessiAzioni);
        }
        if (ch.length + seg.length > 0) {
          setShowPopup(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setChiamate([]);
          setSegnalazioni([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [impianto?.id]);

  return {
    loading,
    chiamate,
    segnalazioni,
    totale: chiamate.length + segnalazioni.length,
    permessiAzioni,
    showPopup,
    setShowPopup,
  };
}

interface AvvisoDoppioniImpiantoSquadraProps {
  impianto: Impianto | null;
  attivita: ReturnType<typeof useAttivitaAperteImpiantoSquadra>;
  onClearImpianto: () => void;
  onParentClose?: () => void;
  contesto?: 'chiamata' | 'segnalazione';
}

export function AvvisoDoppioniImpiantoSquadra({
  impianto,
  attivita,
  onClearImpianto,
  onParentClose,
  contesto = 'chiamata',
}: AvvisoDoppioniImpiantoSquadraProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [confirmSegnalazioneId, setConfirmSegnalazioneId] = useState<string | null>(null);

  if (!impianto || !attivita || attivita.totale === 0) return null;

  const { chiamate, segnalazioni, totale, permessiAzioni, showPopup, setShowPopup } = attivita;

  const handleVaiAChiamata = (chiamataId: string) => {
    setShowPopup(false);
    if (onParentClose) onParentClose();
    navigate(`/chiamata/${chiamataId}`);
  };

  const handlePrendiChiamata = async (c: ChiamataAttivaImpianto) => {
    setActionLoadingId(c.id);
    try {
      const isMiaAssegnata = permessiAzioni.miaSquadraId && c.squadraId === permessiAzioni.miaSquadraId && c.stato === 'assegnata';
      if (isMiaAssegnata) {
        await apiPatch<Chiamata>(`/chiamate/${c.id}/accetta`, {});
        toast({ type: 'success', message: '✅ Chiamata presa in carico!' });
      } else {
        await apiPatch<Chiamata>(`/chiamate/${c.id}/prendi`, {});
        toast({ type: 'success', message: '✅ Chiamata assegnata alla tua squadra!' });
      }
      queryClient.invalidateQueries({ queryKey: ['pending-calls'] });
      queryClient.invalidateQueries({ queryKey: ['chiamate-aperte'] });
      queryClient.invalidateQueries({ queryKey: ['chiamate-da-terminare'] });
      setShowPopup(false);
      if (onParentClose) onParentClose();
      navigate(`/chiamata/${c.id}`);
    } catch (err: any) {
      toast({
        type: 'error',
        message: err?.response?.data?.errore || 'Impossibile prendere in carico la chiamata',
      });
    } finally {
      setActionLoadingId(null);
    }
  };

  const handlePrendiSegnalazione = async (segId: string) => {
    setActionLoadingId(segId);
    try {
      const res = await apiPost<{ success: boolean; chiamata: Chiamata }>(
        `/segnalazioni/${segId}/converti-in-chiamata`,
        {}
      );
      toast({
        type: 'success',
        message: 'Segnalazione presa in carico! Creata chiamata autoassegnata.',
      });
      queryClient.invalidateQueries({ queryKey: ['segnalazioni'] });
      queryClient.invalidateQueries({ queryKey: ['pending-calls'] });
      setConfirmSegnalazioneId(null);
      setShowPopup(false);
      if (onParentClose) onParentClose();
      if (res.data?.chiamata?.id) {
        navigate(`/chiamata/${res.data.chiamata.id}`);
      } else {
        navigate('/');
      }
    } catch (err: any) {
      toast({
        type: 'error',
        message: err?.response?.data?.errore || 'Errore durante la presa in carico della segnalazione',
      });
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <>
      {/* ── BANNER RIEPILOGATIVO PERSISTENTE SOTTO L'IMPIANTO ── */}
      <div className="rounded-2xl border-2 border-amber-400 bg-amber-50 p-3.5 text-amber-950 text-xs animate-fade-in space-y-2 shadow-2xs">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 font-extrabold text-amber-900 text-xs sm:text-sm">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
            <span>
              Attenzione: {totale} {totale === 1 ? 'attività già aperta' : 'attività già aperte'} su questo impianto!
            </span>
          </div>
          <button
            type="button"
            onClick={() => setShowPopup(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-extrabold text-[11px] shadow-xs transition active:scale-95 shrink-0"
          >
            <Eye className="w-3.5 h-3.5" />
            <span>Vedi dettagli e azioni ({totale})</span>
          </button>
        </div>

        <ul className="space-y-1 pl-0.5 text-[11.5px] text-amber-900">
          {chiamate.map((c) => {
            const b = getStatoChiamataSquadraBadge(c, permessiAzioni.miaSquadraId);
            return (
              <li key={c.id} className="flex items-center gap-1.5 flex-wrap">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-extrabold border ${b.classes}`}>
                  {b.label}
                </span>
                <span className="font-bold">{c.guastoSegnalato || c.descrizione}</span>
                {c.squadra?.nome && <span className="text-amber-800 font-semibold">· Sq. {c.squadra.nome}</span>}
                <span className="text-amber-700 text-[11px]">({formatRome(c.dataOra)})</span>
              </li>
            );
          })}
          {segnalazioni.map((s) => (
            <li key={s.id} className="flex items-center gap-1.5 flex-wrap">
              <span className="px-1.5 py-0.5 rounded text-[10px] font-extrabold border bg-amber-100 text-amber-900 border-amber-300">
                SEGNALAZIONE GUASTO
              </span>
              <span className="font-bold">{s.titolo}</span>
              {(s.squadraNome || s.segnalatoDaNome) && (
                <span className="text-amber-800 font-semibold">
                  · da {s.squadraNome || s.segnalatoDaNome}
                </span>
              )}
              <span className="text-amber-700 text-[11px]">({formatRome(s.createdAt)})</span>
            </li>
          ))}
        </ul>
      </div>

      {/* ── POPUP MODALE IMMEDIATO ANTI-DOPPIONE ── */}
      {showPopup &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className="fixed inset-0 z-[115] flex items-center justify-center p-3 sm:p-4 bg-slate-900/80 backdrop-blur-xs animate-fade-in safe-bottom">
            <div className="w-full max-w-lg rounded-2xl sm:rounded-3xl bg-white shadow-2xl flex flex-col max-h-[90dvh] overflow-hidden animate-slide-up border border-amber-300">
              {/* Header */}
              <div className="p-4 sm:p-5 bg-amber-50 border-b border-amber-200 flex items-center justify-between gap-3 shrink-0">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-10 h-10 rounded-2xl bg-amber-500 text-white flex items-center justify-center shrink-0 shadow-xs">
                    <AlertTriangle className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm sm:text-base font-extrabold text-slate-900 leading-tight">
                      Attività già aperte sull&apos;impianto
                    </h3>
                    <p className="text-xs font-bold text-amber-800 truncate">
                      [{impianto.codice}] {impianto.nome}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPopup(false)}
                  className="p-1.5 rounded-full text-slate-500 hover:text-slate-700 hover:bg-amber-100 transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Body scrollabile */}
              <div className="p-4 sm:p-5 space-y-4 overflow-y-auto flex-1">
                <div className="rounded-xl bg-amber-50/80 border border-amber-200 p-3 text-xs text-amber-950 leading-relaxed">
                  Su questo impianto risultano già presenti{' '}
                  <strong>
                    {chiamate.length > 0 &&
                      `${chiamate.length} ${chiamate.length === 1 ? 'chiamata attiva/sospesa' : 'chiamate attive/sospese'}`}
                    {chiamate.length > 0 && segnalazioni.length > 0 && ' e '}
                    {segnalazioni.length > 0 &&
                      `${segnalazioni.length} ${segnalazioni.length === 1 ? 'segnalazione aperta' : 'segnalazioni aperte'}`}
                  </strong>
                  . Controlla prima di inserire un doppione!
                </div>

                {/* SEZIONE CHIAMATE ATTIVE / SOSPESE */}
                {chiamate.length > 0 && (
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-1.5 text-xs font-extrabold text-slate-700 uppercase tracking-wider">
                      <PhoneCall className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Chiamate aperte, in carico o da terminare ({chiamate.length})</span>
                    </div>

                    {chiamate.map((c) => {
                      const badge = getStatoChiamataSquadraBadge(c, permessiAzioni.miaSquadraId);
                      const isMia = !!(permessiAzioni.miaSquadraId && c.squadraId === permessiAzioni.miaSquadraId);
                      const canPrendi =
                        (c.stato === 'nuova' && permessiAzioni.canPrendiAperta) ||
                        (c.stato === 'sospesa' && permessiAzioni.canPrendiDaTerminare) ||
                        (isMia && c.stato === 'assegnata' && !c.inLavorazione);
                      const canApriDettaglio =
                        isMia ||
                        (c.stato === 'nuova' && permessiAzioni.canPrendiAperta) ||
                        (c.stato === 'sospesa' && permessiAzioni.canPrendiDaTerminare);

                      return (
                        <div
                          key={c.id}
                          className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3.5 space-y-2.5 shadow-2xs"
                        >
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-extrabold border ${badge.classes}`}
                            >
                              <span className={`w-2 h-2 rounded-full ${badge.dot}`} />
                              {badge.label}
                            </span>

                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500">
                              <Clock className="w-3 h-3" />
                              {formatRome(c.dataOra)}
                            </span>
                          </div>

                          <div className="text-sm font-extrabold text-slate-900">
                            Guasto: {c.guastoSegnalato || c.descrizione || 'Non specificato'}
                          </div>

                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600">
                            {c.squadra?.nome && (
                              <span className="inline-flex items-center gap-1 font-bold text-sky-800">
                                <Users className="w-3.5 h-3.5" />
                                Squadra: {c.squadra.nome}
                              </span>
                            )}
                            {c.segnalatoDa && (
                              <span>
                                Da: <strong>{c.segnalatoDa}</strong>
                              </span>
                            )}
                          </div>

                          {/* Dettaglio ultimo intervento / motivo sospensione (es. Da Terminare) */}
                          {c.ultimoReport && c.ultimoReport.azioniEseguite && (
                            <div
                              className={`p-2.5 rounded-xl border text-xs space-y-1 ${
                                c.stato === 'sospesa'
                                  ? 'bg-amber-50/90 border-amber-300 text-amber-950'
                                  : 'bg-white border-slate-200 text-slate-700'
                              }`}
                            >
                              <div className="font-extrabold text-[11px] text-amber-800">
                                📋 {c.stato === 'sospesa' ? 'Ultimo intervento / Motivo Da Terminare' : 'Ultimo report'}
                                {c.ultimoReport.squadraNome ? ` (${c.ultimoReport.squadraNome})` : ''}
                                {c.ultimoReport.dataIntervento ? ` · ${formatRome(c.ultimoReport.dataIntervento)}` : ''}:
                              </div>
                              <div className="font-semibold whitespace-pre-wrap leading-snug">
                                {c.ultimoReport.azioniEseguite}
                              </div>
                            </div>
                          )}

                          {/* Azioni rapide sulla chiamata */}
                          {(canPrendi || canApriDettaglio) && (
                            <div className="flex flex-wrap items-center gap-2 pt-1">
                              {canPrendi && (
                                <button
                                  type="button"
                                  disabled={actionLoadingId === c.id}
                                  onClick={() => handlePrendiChiamata(c)}
                                  className="btn-primary py-2 px-3 text-xs font-extrabold gap-1.5 flex-1 justify-center shadow-xs"
                                >
                                  {actionLoadingId === c.id ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  ) : (
                                    <Sparkles className="w-3.5 h-3.5" />
                                  )}
                                  <span>
                                    {isMia
                                      ? 'Prendi in carico ora'
                                      : c.stato === 'sospesa'
                                      ? 'Prendi in carico (Da terminare)'
                                      : 'Assegna a te e apri'}
                                  </span>
                                </button>
                              )}

                              {canApriDettaglio && (
                                <button
                                  type="button"
                                  onClick={() => handleVaiAChiamata(c.id)}
                                  className="btn-outline py-2 px-3 text-xs font-bold gap-1.5 bg-white justify-center"
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                  <span>{isMia ? 'Vai alla chiamata' : 'Vedi dettaglio'}</span>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* SEZIONE SEGNALAZIONI GUASTO APERTE */}
                {segnalazioni.length > 0 && (
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-1.5 text-xs font-extrabold text-slate-700 uppercase tracking-wider">
                      <Wrench className="w-3.5 h-3.5 text-amber-600" />
                      <span>Segnalazioni guasto aperte ({segnalazioni.length})</span>
                    </div>

                    {segnalazioni.map((s) => (
                      <div
                        key={s.id}
                        className="rounded-2xl border border-amber-300 bg-amber-50/50 p-3.5 space-y-2 shadow-2xs"
                      >
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-[11px] font-extrabold bg-amber-100 text-amber-900 border border-amber-300">
                            ⚠️ SEGNALAZIONE APERTA
                          </span>
                          <div className="flex items-center gap-2 text-[11px] font-semibold text-slate-500">
                            {s.fotoCount > 0 && (
                              <span className="inline-flex items-center gap-1 text-sky-700 font-bold">
                                <Camera className="w-3 h-3" />
                                {s.fotoCount} foto
                              </span>
                            )}
                            <span className="inline-flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatRome(s.createdAt)}
                            </span>
                          </div>
                        </div>

                        <div className="text-sm font-extrabold text-slate-900">
                          Anomalia: {s.titolo}
                        </div>

                        {s.descrizione && (
                          <div className="text-xs text-slate-700 whitespace-pre-wrap leading-snug">
                            {s.descrizione}
                          </div>
                        )}

                        <div className="text-[11px] text-slate-500">
                          Rilevato da: <strong>{s.squadraNome || s.segnalatoDaNome || 'Tecnico'}</strong>
                        </div>

                        {permessiAzioni.canPrendiSegnalazione && (
                          <div className="pt-1">
                            {confirmSegnalazioneId === s.id ? (
                              <div className="p-2.5 rounded-xl bg-amber-100/90 border border-amber-300 space-y-2">
                                <p className="text-xs font-bold text-amber-950">
                                  Prendi in carico questa segnalazione solo se hai intenzione di andare a fare l&apos;intervento!
                                </p>
                                <div className="flex justify-end gap-2">
                                  <button
                                    type="button"
                                    onClick={() => setConfirmSegnalazioneId(null)}
                                    disabled={actionLoadingId === s.id}
                                    className="btn-outline py-1.5 px-3 text-xs font-bold bg-white"
                                  >
                                    annulla
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handlePrendiSegnalazione(s.id)}
                                    disabled={actionLoadingId === s.id}
                                    className="btn-primary bg-amber-600 hover:bg-amber-700 py-1.5 px-3 text-xs font-extrabold gap-1"
                                  >
                                    {actionLoadingId === s.id ? (
                                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    ) : (
                                      <Sparkles className="w-3.5 h-3.5" />
                                    )}
                                    <span>prendi in carico</span>
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setConfirmSegnalazioneId(s.id)}
                                className="w-full btn-primary py-2 text-xs font-extrabold flex items-center justify-center gap-1.5 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 shadow-xs"
                              >
                                <Sparkles className="w-3.5 h-3.5" />
                                <span>⚡ Prendi in carico ed esegui intervento</span>
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-slate-200 bg-slate-50 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setShowPopup(false);
                    onClearImpianto();
                  }}
                  className="btn-outline py-2.5 px-4 text-xs font-extrabold text-rose-700 border-rose-200 hover:bg-rose-50 gap-1.5 justify-center"
                >
                  <XCircle className="w-4 h-4 text-rose-600" />
                  <span>Cambia impianto / Annulla</span>
                </button>

                <button
                  type="button"
                  onClick={() => setShowPopup(false)}
                  className="btn-primary bg-amber-600 hover:bg-amber-700 py-2.5 px-4 text-xs font-extrabold gap-1.5 justify-center shadow-sm"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span>
                    Procedi comunque ({contesto === 'segnalazione' ? 'nuova segnalazione' : 'nuova chiamata'})
                  </span>
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
